import { supabase } from '@/integrations/supabase/client';
import { logger } from './logger';

interface DeviceInfo {
  fingerprintId: string;
  browser: string;
  os: string;
  deviceName: string;
  deviceType?: string;
  clientPlatform?: string;
  timezone?: string | null;
  userAgent: string;
}

/** Date et heure lisibles, dans le fuseau de l'appareil qui lit la notification. */
const frDateTime = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * Lien de la notification : ouvre la page « Mes appareils connectés » avec
 * l'appareil concerné pré-sélectionné, prêt à être révoqué en un tap.
 */
export const deviceAlertUrl = (deviceId: string) =>
  `/employee/settings/appareils?device=${encodeURIComponent(deviceId)}`;

/**
 * Crée une notification utilisateur pour une connexion depuis un nouvel appareil
 */
export const notifyNewDeviceLogin = async (
  userId: string,
  deviceId: string,
  deviceInfo: DeviceInfo,
  organizationId?: string
): Promise<void> => {
  const detectedAt = new Date().toISOString();
  const context = [deviceInfo.deviceType, deviceInfo.clientPlatform, deviceInfo.browser]
    .filter(Boolean)
    .join(' · ');

  try {
    // 1. Créer une notification utilisateur
    const { error: notifError } = await supabase
      .from('user_notifications')
      .insert({
        user_id: userId,
        organization_id: organizationId || null,
        // Types autorisés par la contrainte user_notifications_type_check
        type: 'warning',
        title: '🔐 Nouvelle connexion détectée',
        message: `Connexion depuis un nouvel appareil : ${deviceInfo.deviceName} (${context}) le ${frDateTime(detectedAt)}. Si ce n'est pas toi, révoque-le tout de suite.`,
        action_url: deviceAlertUrl(deviceId),
        metadata: {
          deviceId,
          deviceName: deviceInfo.deviceName,
          deviceType: deviceInfo.deviceType ?? null,
          clientPlatform: deviceInfo.clientPlatform ?? null,
          timezone: deviceInfo.timezone ?? null,
          browser: deviceInfo.browser,
          os: deviceInfo.os,
          detectedAt,
        },
      });
    
    if (notifError) {
      logger.error('[DEVICE NOTIF] Error creating user notification:', notifError);
    } else {
      logger.debug('[DEVICE NOTIF] User notification created for new device');
    }
    
    // 2. Créer une alerte de sécurité (pour les admins)
    const { error: alertError } = await supabase.rpc('create_security_alert', {
      // Autorisé par security_alerts_alert_type_check ; le motif précis est dans p_metadata.kind
      p_alert_type: 'suspicious_activity',
      p_severity: 'medium',
      p_title: 'Nouvelle connexion depuis un appareil inconnu',
      p_description: `Connexion détectée depuis un nouvel appareil: ${deviceInfo.deviceName} (${deviceInfo.browser} sur ${deviceInfo.os})`,
      p_actor_id: userId,
      p_affected_users: [userId],
      p_affected_resources: {
        deviceId,
        deviceName: deviceInfo.deviceName,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
      },
      p_metadata: {
        kind: 'new_device',
        fingerprint_partial: deviceInfo.fingerprintId.slice(0, 8),
        timestamp: new Date().toISOString(),
      },
      p_organization_id: organizationId || null,
    });
    
    if (alertError) {
      logger.error('[DEVICE NOTIF] Error creating security alert:', alertError);
    } else {
      logger.debug('[DEVICE NOTIF] Security alert created for new device');
    }
    
  } catch (error) {
    logger.error('[DEVICE NOTIF] Error in notifyNewDeviceLogin:', error);
    // Ne pas bloquer le login si la notification échoue
  }
};
